import Phaser from 'phaser';
import { gameState } from '@/state/store';

export type AutoSpeedNivaa = 0 | 1 | 2;

const STORAGE_KEY = 'forlagshuset:autoSpeed';
const DELAY_PER_NIVAA: Record<AutoSpeedNivaa, number> = {
  0: 0,
  1: 3000,
  2: 1500,
};

type Listener = (nivaa: AutoSpeedNivaa) => void;

export class AutoSpeed {
  private nivaa: AutoSpeedNivaa = 0;
  private timer?: Phaser.Time.TimerEvent;
  private scene?: Phaser.Scene;
  private listeners: Set<Listener> = new Set();
  private pauset = false;

  constructor() {
    this.nivaa = this.lestLagret();
  }

  get verdi(): AutoSpeedNivaa {
    return this.nivaa;
  }

  attachScene(scene: Phaser.Scene): void {
    this.scene = scene;
    if (this.nivaa > 0) this.startTimer();
  }

  detachScene(): void {
    this.stoppTimer();
    this.scene = undefined;
  }

  cycle(): AutoSpeedNivaa {
    const neste = ((this.nivaa + 1) % 3) as AutoSpeedNivaa;
    this.settNivaa(neste);
    return neste;
  }

  settNivaa(nivaa: AutoSpeedNivaa): void {
    if (this.nivaa === nivaa) return;
    this.nivaa = nivaa;
    this.lagre(nivaa);
    this.stoppTimer();
    this.pauset = false;
    if (nivaa > 0 && this.scene) this.startTimer();
    this.emit();
  }

  paus(): void {
    if (this.nivaa === 0) return;
    this.pauset = true;
    this.stoppTimer();
  }

  fortsett(): void {
    if (!this.pauset) return;
    if (this.nivaa === 0) return;
    if (gameState.aktivtEvent) return;
    this.pauset = false;
    if (this.scene) this.startTimer();
  }

  erPauset(): boolean {
    return this.pauset;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.nivaa);
    return () => this.listeners.delete(fn);
  }

  symbol(): string {
    if (this.nivaa === 0) return '⏸';
    if (this.nivaa === 1) return '▶';
    return '⏩';
  }

  delay(): number {
    return DELAY_PER_NIVAA[this.nivaa];
  }

  private startTimer(): void {
    if (!this.scene) return;
    if (this.nivaa === 0) return;
    if (this.pauset) return;
    this.stoppTimer();
    const delay = this.delay();
    this.timer = this.scene.time.addEvent({
      delay,
      loop: true,
      callback: () => this.tick(),
    });
  }

  private stoppTimer(): void {
    this.timer?.remove(false);
    this.timer = undefined;
  }

  private tick(): void {
    if (this.nivaa === 0) return;
    if (this.pauset) return;
    if (gameState.aktivtEvent) {
      this.paus();
      return;
    }
    gameState.nesteDag();
  }

  private emit(): void {
    for (const fn of this.listeners) fn(this.nivaa);
  }

  private lestLagret(): AutoSpeedNivaa {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw === '1') return 1;
      if (raw === '2') return 2;
      return 0;
    } catch {
      return 0;
    }
  }

  private lagre(nivaa: AutoSpeedNivaa): void {
    try {
      localStorage.setItem(STORAGE_KEY, String(nivaa));
    } catch {
      // ignore
    }
  }
}

export const autoSpeed = new AutoSpeed();
